import { nanoid } from '@reduxjs/toolkit';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { captureSidekickActingOrganizationScope } from '../api/actingOrganization';
import { useConversationServiceStopScopedConversationMutation } from '../api/overrides';
import {
  SIDEKICK_FEATURE_EVENT,
  useSidekickFeatureEvent,
} from '../featureEvents';
import { useIsSidekickSseEnabled } from '../flag/useIsSidekickSseEnabled';
import {
  finishPendingStop,
  selectConversationId,
  selectIsStopPending,
  selectIsStreaming,
  startPendingStop,
} from '../state/aiSidekickChatSlice';

/** Result of {@link useStopResponse}. */
export type StopResponse = {
  /** Stops the running response; does nothing while none runs. */
  stopResponse: () => void;
  /**
   * Whether a stop for the current conversation is still on its way to the
   * backend. The stop applies to the whole conversation, so new questions in
   * it wait for the stop to settle rather than risk being stopped by it.
   */
  isStopPending: boolean;
};

/**
 * Stops the running response. The turn ends on screen right away, and the
 * backend is asked to stop a streamed turn once it has acknowledged the
 * question. A question still waiting on its new conversation is never sent.
 * @param stopTurn - Ends the turn locally and resolves once the backend can be
 * asked to stop it.
 * @param cancelPendingSubmission - Keeps a question that has not started
 * sending from being sent.
 * @returns The stop callback and whether a stop is still pending.
 */
export const useStopResponse = (
  stopTurn: () => Promise<void>,
  cancelPendingSubmission: () => void
): StopResponse => {
  const dispatch = useDispatch();
  const conversationId = useSelector(selectConversationId);
  const isStreaming = useSelector(selectIsStreaming);
  const isStopPending = useSelector(selectIsStopPending);
  const isSseEnabled = useIsSidekickSseEnabled();
  const [stopConversation] =
    useConversationServiceStopScopedConversationMutation();
  const emitFeatureEvent = useSidekickFeatureEvent();

  const stopResponse = useCallback((): void => {
    if (!isStreaming) {
      return;
    }
    emitFeatureEvent({ type: SIDEKICK_FEATURE_EVENT.RESPONSE_STOPPED });
    cancelPendingSubmission();
    const organizationScope = captureSidekickActingOrganizationScope();
    const stopId = nanoid();
    dispatch(startPendingStop({ id: stopId, conversationId }));
    // Best effort: the design shows no message either way, so a failed stop
    // call is not surfaced. The backend only stops streamed turns; a sync
    // turn ends here when its request is aborted.
    void stopTurn()
      .then(() =>
        conversationId === null || !isSseEnabled
          ? undefined
          : stopConversation({ conversationId, organizationScope }).unwrap()
      )
      .catch(() => undefined)
      .finally(() => {
        dispatch(finishPendingStop(stopId));
      });
  }, [
    cancelPendingSubmission,
    conversationId,
    dispatch,
    emitFeatureEvent,
    isSseEnabled,
    isStreaming,
    stopConversation,
    stopTurn,
  ]);

  return useMemo(
    () => ({ stopResponse, isStopPending }),
    [isStopPending, stopResponse]
  );
};
